import React, { MouseEvent, useEffect, useRef, useState } from "react";
import "./CommonDropDown.css";
import CheckBox from "../../Buttons/CheckBox";

export interface CommonDropDownDisplayValue<T> {
  icon?: React.ReactNode;
  display: string;
  value: T;
  disabled: boolean;
}

export interface GroupedDropDownDisplayValue<T> {
  title: string;
  displayValues: CommonDropDownDisplayValue<T>[];
}

type Props<T> = {
  values: T[];
  id: string;
  onChange: (values: T[]) => void;
  dropDownDisplayValues: GroupedDropDownDisplayValue<T>[];
  style?: React.CSSProperties;
  dropDownStyle?: React.CSSProperties;
  disabled?: boolean;
  hideDropIcon?: boolean;
  dropDirection?: "down" | "up";
  colorVariant?: "default" | "alternate";
};

type DropDownProps = {
  options: React.ReactNode;
  onBlur: () => void;
  style?: React.CSSProperties;
};

const isDomParent = (child: Node, parent: Node) => {
  return parent.contains(child) || parent.parentNode?.contains(child);
};

const MultipleSelectDropDownOptions = ({
  options,
  onBlur = () => {},
  style = {},
}: DropDownProps) => {
  const container = useRef(null);

  const onOutsieClicked = (event: MouseEvent) => {
    const containerDom = container.current
      ? (container.current as unknown)
      : {};
    const targetDom = event.target;
    const check = isDomParent(targetDom as Node, containerDom as Node);
    if (check) {
      event.preventDefault();
    } else {
      onBlur();
    }
  };

  const savedHandler = useRef(onOutsieClicked);

  useEffect(() => {
    const eventListener = (event: any) => savedHandler.current(event);
    document.addEventListener("mousedown", eventListener);
    return () => document.removeEventListener("mousedown", eventListener);
  }, []);

  return (
    <div
      className={"common-dropdown-container common-dropdown-active"}
      ref={container}
      style={style}
    >
      {options}
    </div>
  );
};

export default function MultipleSelectDropDown<T extends string | number>({
  values,
  id,
  onChange,
  dropDownDisplayValues,
  style = {},
  dropDownStyle = {},
  disabled = false,
  hideDropIcon = false,
  dropDirection = "down",
  colorVariant = "default",
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const selectorHeight = style.height;
  const fontSize = style.fontSize;

  let allOptionValues = dropDownDisplayValues.reduce((result, dropDown) => {
    if (dropDown.displayValues.length > 0) {
      return result.concat(
        dropDown.displayValues
          .filter((dpv) => !dpv.disabled)
          .map((dpv) => dpv.value)
      );
    } else {
      return result;
    }
  }, [] as T[]);

  const onChangeSelect = (value: T) => {
    let selectedSet = new Set(values);

    if (selectedSet.has(value)) {
      selectedSet.delete(value);
    } else {
      selectedSet.add(value);
    }

    onChange(Array.from(selectedSet));
  };

  const onChangeSelectAll = () => {
    let selectedSet = new Set(values);

    if (selectedSet.size === allOptionValues.length) {
      selectedSet.clear();
    } else if (selectedSet.size >= 0) {
      selectedSet = new Set(allOptionValues);
    } else {
      selectedSet.clear();
    }

    onChange(Array.from(selectedSet));
  };

  const deselectAll = () => {
    onChange([]);
  };

  const options: JSX.Element[] = [];

  options.push(
    <div
      key={`dropdown-select-all`}
      className={
        colorVariant === "alternate"
          ? "common-dropdown-item color-alternate"
          : "common-dropdown-item"
      }
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onChangeSelectAll();
        }
      }}
      style={{
        alignItems: "center",
        height: selectorHeight,
        fontSize: fontSize,
        marginTop: 4,
        marginBottom: 4,
      }}
    >
      <div className="common-dropdown-checkbox">
        <CheckBox
          checked={values.length === allOptionValues.length}
          id={`${id}-select-all-checkbox`}
          name={`${id}-select-all-checkbox`}
          onChange={onChangeSelectAll}
          colorVariant={colorVariant}
          size="small"
        />
      </div>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onChangeSelectAll();
          }
        }}
        className="common-dropdown-text-container"
      >
        Select All
      </div>
    </div>
  );

  dropDownDisplayValues.forEach((dropDown) => {
    options.push(
      <div
        key={`dropdown-title-${dropDown.title}`}
        className={"common-dropdown-item no-hover"}
        style={
          dropDown.title
            ? {
                alignItems: "center",
                height: selectorHeight,
                fontSize: fontSize,
                marginTop: 13,
                marginBottom: 2,
              }
            : { display: "none" }
        }
      >
        <div className="common-dropdown-title">{dropDown.title}</div>
      </div>
    );

    dropDown.displayValues.forEach((displayValue) => {
      let itemClass = "common-dropdown-item";
      if (displayValue.disabled) {
        itemClass += " disabled";
      }
      if (colorVariant === "alternate") {
        itemClass += " color-alternate";
      }

      options.push(
        <div
          key={`dropdown-${displayValue.value}`}
          className={itemClass}
          onClick={(e) => {
            if (!displayValue.disabled && e.target === e.currentTarget) {
              onChangeSelect(displayValue.value);
            }
          }}
          style={{
            alignItems: "center",
            height: selectorHeight,
            fontSize: fontSize,
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          <div className="common-dropdown-checkbox">
            <CheckBox
              checked={values.includes(displayValue.value)}
              id={`${id}-${displayValue.value}-checkbox`}
              name={`${id}-${displayValue.value}-checkbox`}
              onChange={() => {
                if (!displayValue.disabled) {
                  onChangeSelect(displayValue.value);
                }
              }}
              colorVariant={colorVariant}
              size="small"
            />
          </div>
          <div className="common-dropdown-icon-container">
            {displayValue.icon}
          </div>
          <div className="common-dropdown-text-container">
            <p
              onClick={(e) => {
                if (!displayValue.disabled && e.target === e.currentTarget) {
                  onChangeSelect(displayValue.value);
                }
              }}
            >
              {displayValue.display}
            </p>
          </div>
        </div>
      );
    });
  });

  let dropStyle = dropDownStyle;
  if (dropDirection === "up") {
    if (dropStyle.height) {
      if (typeof dropStyle.height === "number") {
        dropStyle = { ...dropStyle, top: `${-dropStyle.height}px` };
      } else {
        dropStyle = { ...dropStyle, top: `-${dropStyle.height}` };
      }
    } else {
      dropStyle = {
        ...dropStyle,
        top: `${dropDownDisplayValues.length * -100 + 1}%`,
      };
    }

    if (dropStyle.maxHeight) {
      if (typeof dropStyle.maxHeight === "number") {
        dropStyle = { ...dropStyle, top: `${-dropStyle.maxHeight}px` };
      } else {
        dropStyle = { ...dropStyle, top: `-${dropStyle.maxHeight}` };
      }
    } else {
      dropStyle = {
        ...dropStyle,
        top: `${dropDownDisplayValues.length * -100 + 1}%`,
      };
    }
  }

  const onClickContainer = () => {
    setOpen(!open);
  };

  const onBlur = () => {
    setOpen(false);
  };

  const displayString =
    values.length === 1
      ? "Single"
      : values.length === allOptionValues.length
      ? "All"
      : values.length > 0
      ? "Multiple"
      : "Not selected";

  return (
    <div
      style={{
        position: "relative",
        ...style,
      }}
    >
      <button
        className="common-dropdown-button"
        id={id}
        key={id + "container"}
        onClick={onClickContainer}
        style={style}
        disabled={disabled}
      >
        <label style={{ fontSize: fontSize }}>{displayString}</label>
        <div className="common-dropdown-arrow-multi">
          {values.length > 0 && (
            <div onClick={deselectAll} className="close-button">
              <svg
                width="12"
                height="12"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.9986 18L0 16.0014L7.00139 9.00007L0 1.99867L1.9986 0L9 7.0014L16.0014 0L18 1.9986C15.2118 4.81136 13.7868 6.18724 10.9986 9L18 16.0014L16.0014 18L9 10.9986L1.9986 18Z"
                  fill="#313131"
                />
                <path
                  d="M1.9986 18L0 16.0014L7.00139 9.00007L0 1.99867L1.9986 0L9 7.0014L16.0014 0L18 1.9986C15.2118 4.81136 13.7868 6.18724 10.9986 9L18 16.0014L16.0014 18L9 10.9986L1.9986 18Z"
                  fill="#313131"
                />
              </svg>
            </div>
          )}
          {!hideDropIcon ? (
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.99994 6L0.669812 -7.23134e-07L9.33007 0L4.99994 6Z"
                fill="#707070"
              />
            </svg>
          ) : (
            <></>
          )}
        </div>
      </button>
      {open && (
        <MultipleSelectDropDownOptions
          options={options}
          onBlur={onBlur}
          style={dropStyle}
        />
      )}
    </div>
  );
}
